import { apiError } from "../../../../../../../utils/formErrors";
import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Stack, CircularProgress } from "@mui/material";
import TopBar from "../../../../../../../component/TopBar";
import MainBox from "../../../../../container/MainBox";
import BtnPrimary from "../../../../../../../component/PrimaryButton";
import BtnSecondary from "../../../../../../../component/SecondaryButton";
import SuccessDialog from "../../../../../../../component/SuccessDialog";
import SectionLoader from "../../../../../../../component/SectionLoader";
import EmptyState from "../../../../../../../component/EmptyState";
import { getPolicyById, publishPolicy } from "../../../../../../../services/expense";
import { useNotify } from "../../../../../../../component/NotificationProvider";
import { tokens } from "../../../../../../../theme";

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-GB") : "—");

const Fact = ({ label, value }) => (
  <Box>
    <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted, textTransform: "uppercase", letterSpacing: "0.03em" }}>{label}</Box>
    <Box sx={{ fontSize: "0.9375rem", fontWeight: 600, color: tokens.text, mt: "0.25rem" }}>{value ?? "—"}</Box>
  </Box>
);
const Grid = ({ children }) => (
  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" }, gap: "1.25rem", mb: "1.5rem" }}>{children}</Box>
);
const Section = ({ title, children }) => (
  <Box sx={{ mb: "1.5rem" }}>
    <Box sx={{ fontSize: "1rem", fontWeight: 600, color: tokens.text, mb: "1rem", pb: "0.5rem", borderBottom: `1px solid ${tokens.border}` }}>{title}</Box>
    {children}
  </Box>
);

const ExpensePolicyReview = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const notify = useNotify();
  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [publishing, setPublishing] = useState(false);
  const [done, setDone] = useState(false);
  const policyId = state?.policyId;

  useEffect(() => {
    if (!policyId) { setLoading(false); return; }
    (async () => {
      try {
        const res = await getPolicyById(policyId);
        setPolicy(res?.data);
      } catch (e) { notify.error(e?.message || "Failed to load policy"); }
      finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [policyId]);

  const publish = async () => {
    setPublishing(true);
    try {
      const res = await publishPolicy(policyId);
      if (res?.success === false) throw apiError(res, "Could not publish");
      setDone(true);
    } catch (e) { notify.error(e?.message || "Could not publish policy"); }
    finally { setPublishing(false); }
  };

  if (!policyId) return <Box sx={{ width: "100%" }}><TopBar MainHeading={"Expense Policy"} /><EmptyState title="No policy to review" /></Box>;

  const cats = policy?.policyCateogary || [];
  const chain = policy?.approvalChain || [];

  return (
    <Box sx={{ width: "100%" }}>
      <TopBar MainHeading={"Expense Policy"} breadcrumbs={[{ label: "Expense Policy", href: "/app/expense-policy/listing" }, { label: "Review" }]} />
      <MainBox sx={{ padding: "2rem 1.25rem" }}>
        {loading ? <SectionLoader /> : !policy ? <EmptyState title="Policy not found" /> : (
          <>
            <Box sx={{ display: "inline-flex", alignItems: "center", gap: "0.5rem", mb: "1.5rem", px: "0.75rem", py: "0.5rem", borderRadius: "999px", background: tokens.warningTint }}>
              <Box sx={{ width: "0.5rem", height: "0.5rem", borderRadius: "50%", background: tokens.warning }} />
              <Box sx={{ fontSize: "0.8125rem", fontWeight: 600, color: tokens.text }}>Draft — not claimable until published</Box>
            </Box>

            <Section title="Policy details">
              <Grid>
                <Fact label="Name" value={policy.name} />
                <Fact label="Code" value={policy.code} />
                <Fact label="Validity" value={`${fmtDate(policy.startDate)} → ${fmtDate(policy.endDate)}`} />
              </Grid>
            </Section>

            <Section title={`Categories (${cats.length})`}>
              <Grid>
                {cats.map((c, i) => (
                  <Fact key={i} label={c.name || "Category"}
                    value={c.claimType === "mileage" ? `Mileage · ₹${c.ratePerKm}/km`
                      : c.claimType === "perDiem" ? `Per-diem · ₹${c.dailyRate}/day`
                      : c.limits?.claimLimit ? `Limit ₹${c.limits.claimLimit}` : "Flat"} />
                ))}
                {cats.length === 0 && <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted }}>No categories</Box>}
              </Grid>
            </Section>

            <Section title="Approval">
              <Grid>
                {chain.length > 0
                  ? chain.slice().sort((a, b) => a.level - b.level).map((l, i) => (
                      <Fact key={i} label={`Level ${l.level}${l.minAmount ? ` (≥ ₹${l.minAmount})` : ""}`} value={`${(l.approvers || []).length} approver(s)`} />
                    ))
                  : <Fact label="Approvers" value={`${(policy.approverIds || []).length} (single level)`} />}
              </Grid>
            </Section>

            <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mt: "1rem" }}>
              <BtnSecondary to="/app/expense-policy/listing" sx={{ borderRadius: "8px" }}>Save as draft</BtnSecondary>
              <BtnPrimary onClick={publish} disabled={publishing} sx={{ borderRadius: "8px" }}>
                {publishing ? <CircularProgress size={"1.25rem"} /> : "Publish policy"}
              </BtnPrimary>
            </Stack>
          </>
        )}

        <SuccessDialog open={done} onClose={() => setDone(false)} message={"Expense policy published!"}
          addNewLink={"/app/expense-policy/new/setting"} addNewText="Create New Policy"
          viewAllLink={"/app/expense-policy/listing"} viewAllText="View All Policies" />
      </MainBox>
    </Box>
  );
};

export default ExpensePolicyReview;
